import { useFeatureRollout } from '@rea-argonaut/experiment';
import {
  Commute,
  mediaHelper,
  MultiIntentSearchModal,
  paletteHelper,
  radiusHelper,
  SearchFlowType,
  MOBILE_MAX,
} from '@property.com.au/ui-kit';
import { FunctionComponent } from 'react';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import SearchSvg from '../../../../../assets/search.svg';
import { CommuteSearchCTAs } from '../CommuteSearchCTAs/CommuteSearchCTAs';
import {
  MultiIntentSearchModalButton,
  multiIntentSearchModalButtonIconClassName,
} from './MultiIntentSearchModalButton.styling';
import { replace } from '@rea-argonaut/link';
import { trackModalImpression } from '../../../../../helpers/analytics/trackModalImpression';
import { HOME_TRACKING_SOURCE } from '../../../../../helpers/tracking';
import { getEnv } from '../../../../../helpers/getEnv';
import { trackSearchRefinementModalOpen } from '../../../../../helpers/analytics/trackSearchRefinementModalOpen';
import { isHtmlButtonRef } from '../../../../../helpers/isHtmlButtonRef';

const homePageSearchButtonLabelClassName = 'home-page-search-button-label';

const PropertySearchContainer = styled.div`
  width: 100%;
`;

const IconWrapper = styled.div`
  color: ${paletteHelper('textInverted')};
  height: 40px;
  width: 57px;
  border-radius: ${radiusHelper('full')};
  display: flex;
  justify-content: center;
  align-items: center;

  ${mediaHelper('wideMin')} {
    width: 68px;
    height: 47px;
  }
`;

const SearchIcon = styled.img``;

const MobileSearchBarLabel = styled.p`
  ${mediaHelper('desktopMin')} {
    display: none;
  }
`;

const DesktopSearchBarLabel = styled.p`
  ${mediaHelper('tabletMax')} {
    display: none;
  }
`;

export const PropertySearch: FunctionComponent = () => {
  const [isMultiIntentSearchModalOpen, setIsMultiIntentSearchModalOpen] = useState(false);
  const [initialSearchFlow, setInitialSearchFlow] = useState<SearchFlowType>('LocationSearch');
  const [initialCommute, setInitialCommute] = useState<Commute | undefined>(undefined);

  const openCommuteSearch = (commute?: Commute) => {
    setInitialSearchFlow('CommuteSearch');
    setIsMultiIntentSearchModalOpen(true);
    setInitialCommute(commute);
  };

  const openLocationSearch = () => {
    setInitialSearchFlow('LocationSearch');
    setIsMultiIntentSearchModalOpen(true);
  };

  const closeMultiIntentSearchModal = () => {
    setIsMultiIntentSearchModalOpen(false);
  };

  const multiIntentSearchModalButtonRef = useRef<HTMLButtonElement>(null);
  const { isEnabled: isCommuteSearchEnabled } = useFeatureRollout('commute_search');
  const { isEnabled: areCommuteSearchCTAsEnabled } = useFeatureRollout('commute_search_ctas');

  const showCommuteSearchCTAs = isCommuteSearchEnabled() && areCommuteSearchCTAsEnabled();

  useEffect(() => {
    if (location.hash === '#search-traveltime') {
      openCommuteSearch();
      trackModalImpression({
        modalName: 'multi_intent',
        screenName: 'commute_search_point_of_interest_search_screen',
        inPageSource: 'url-fragment:travel-time',
        isModalFullscreen: window.innerWidth <= MOBILE_MAX,
      });
      replace(location.pathname + location.search);
    }
    if (location.hash === '#search-default') {
      openLocationSearch();
      trackSearchRefinementModalOpen();
      replace(location.pathname + location.search);
    }
  }, []);

  return (
    <>
      {isHtmlButtonRef(multiIntentSearchModalButtonRef) && (
        <MultiIntentSearchModal
          opened={isMultiIntentSearchModalOpen}
          onClose={() => closeMultiIntentSearchModal()}
          pageSource={HOME_TRACKING_SOURCE}
          data-testid="home-page-multi-intent-search-modal"
          buttonRef={multiIntentSearchModalButtonRef}
          isSignedIn={false}
          publicExperienceApiUrl={getEnv.PUBLIC_EXPERIENCE_API_URL()}
          rootElementId="argonaut-wrapper"
          googleApiKey={getEnv.GOOGLE_API_KEY()}
          enableCommuteSearch={isCommuteSearchEnabled()}
          initialSearchFlow={initialSearchFlow}
          initialCommute={initialCommute}
        />
      )}
      <PropertySearchContainer>
        <MultiIntentSearchModalButton
          data-testid="home-page-multi-intent-search-modal-button"
          dangerouslySetClassNames={{
            label: homePageSearchButtonLabelClassName,
          }}
          onClick={() => {
            openLocationSearch();
            trackSearchRefinementModalOpen();
          }}
          forwardedAs="button"
          icon={
            <IconWrapper className={multiIntentSearchModalButtonIconClassName}>
              <SearchIcon alt="search-icon" src={SearchSvg} />
            </IconWrapper>
          }
          variant="outline-secondary"
          iconPlacement="right"
          ref={multiIntentSearchModalButtonRef}
        >
          <MobileSearchBarLabel>Address, suburb or state</MobileSearchBarLabel>
          <DesktopSearchBarLabel>Search an address, suburb or state</DesktopSearchBarLabel>
        </MultiIntentSearchModalButton>

        {showCommuteSearchCTAs && <CommuteSearchCTAs openCommuteSearch={openCommuteSearch} />}
      </PropertySearchContainer>
    </>
  );
};
